import React, { useState } from "react";
import SidebarNav from "./SidebarNav";
import {
	Box,
	Typography,
	TextField,
	Button,
	Paper,
	Container,
	Alert,
	Snackbar,
	CircularProgress,
} from "@mui/material";
import { Send, Support as SupportIcon, Email } from "@mui/icons-material";

const Support = () => {
	const [description, setDescription] = useState(
		`I would like ... functionality to be added to the app.
I have ... question about pricing.
I am having ... issues with payment or video creation.`
	);
	const [loading, setLoading] = useState(false);
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const handleDescriptionChange = (e) => {
		setDescription(e.target.value);
	};

	const handleCloseNotification = () => {
		setNotification({ ...notification, open: false });
	};

	const handleSubmitSupportMessage = async () => {
		setLoading(true);

		// Input validation
		if (description.length > 170) {
			setNotification({
				open: true,
				message:
					"Shorten the message (<170 words). We'll get back to you to discuss in detail and resolve any issues.",
				severity: "error",
			});
			setLoading(false);
			return;
		}

		try {
			// Get user authentication token
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =
				"https://pmco1162s7.execute-api.us-east-1.amazonaws.com/prod";
			const body = {
				task: "submitMagicVerseSupportMsg",
				msg: description,
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();
			console.log(data);

			if (data.code === 200) {
				setNotification({
					open: true,
					message:
						"Your message has been submitted. You'll receive an email (within 24 hours) responding to your request shortly.",
					severity: "success",
				});
			} else {
				setNotification({
					open: true,
					message: "Something went wrong. Please try again later.",
					severity: "error",
				});
			}
		} catch (err) {
			console.log(err.message);
			setNotification({
				open: true,
				message: "An error occurred. Please try again later.",
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Container maxWidth="md" className="py-8">
			<Paper elevation={3} className="rounded-lg overflow-hidden">
				<Box className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
					<Box className="flex items-center justify-center mb-2">
						<SupportIcon fontSize="large" className="mr-2" />
						<Typography variant="h4" className="font-bold">
							Contact Support
						</Typography>
					</Box>
					<Typography variant="subtitle1" className="text-center">
						Tell us how we can help you
					</Typography>
				</Box>

				<Box className="p-6 bg-white">
					<TextField
						value={description}
						onChange={handleDescriptionChange}
						multiline
						rows={8}
						fullWidth
						variant="outlined"
						placeholder="Describe your issue or request..."
						className="mb-4"
						InputProps={{
							className: "font-medium",
						}}
					/>

					<Box className="flex items-center justify-between mt-4">
						<Typography
							variant="caption"
							color="textSecondary"
							className="text-gray-500"
						>
							Max 170 characters. {170 - description.length}{" "}
							remaining.
						</Typography>

						<Button
							variant="contained"
							color="primary"
							onClick={handleSubmitSupportMessage}
							startIcon={
								loading ? (
									<CircularProgress
										size={20}
										color="inherit"
									/>
								) : (
									<Send />
								)
							}
							disabled={loading}
							className="py-2 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-md"
						>
							{loading ? "Submitting..." : "Submit Message"}
						</Button>
					</Box>

					<Box className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
						<Box className="flex items-start">
							<Email className="text-blue-500 mr-3 mt-1" />
							<Box>
								<Typography
									variant="subtitle2"
									className="font-medium text-blue-700"
								>
									What happens next?
								</Typography>
								<Typography
									variant="body2"
									className="text-blue-600 mt-1"
								>
									Our support team will review your message
									and respond within 24 hours via email. For
									urgent issues, please include details that
									help us understand your situation better.
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>
			</Paper>

			<Snackbar
				open={notification.open}
				autoHideDuration={6000}
				onClose={handleCloseNotification}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleCloseNotification}
					severity={notification.severity}
					variant="filled"
					className="w-full"
				>
					{notification.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default Support;
